import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { lifeInsurancePage, companyInfo } from '../mock';
import { CheckCircle2, Phone, MessageCircle, Heart, FileText, AlertCircle, ExternalLink, Users, Home, GraduationCap, Shield, HelpCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const LifeInsurance = () => {
  const { language } = useLanguage();
  const content = lifeInsurancePage[language];
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [widgetError, setWidgetError] = useState(false);
  const containerRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  const whatsappUrl = `https://wa.me/${companyInfo.phoneWhatsApp}?text=${encodeURIComponent(content.whatsappMessage)}`;

  const iconMap = {
    Users,
    Home,
    GraduationCap,
    Heart
  };

  useEffect(() => {
    // Check if script is already loaded
    const existingScript = document.getElementById('strife');
    
    if (existingScript && scriptLoadedRef.current) {
      setWidgetLoaded(true);
      return;
    }

    // Load the Quote & Apply widget script - only once
    const loadWidget = () => {
      try {
        // Ensure QUOTE_AND_APPLY_STRIFE_KEY is available
        const strifeKey = process.env.REACT_APP_QUOTE_AND_APPLY_STRIFE_KEY;
        
        if (!strifeKey) {
          console.error('QUOTE_AND_APPLY_STRIFE_KEY not found in environment');
          setWidgetError(true);
          return;
        }

        // Create script element
        const script = document.createElement('script');
        script.id = 'strife';
        script.src = 'https://cdn.quoteandapply.io/widget.js';
        script.setAttribute('data-strife-key', strifeKey);
        script.setAttribute('data-strife-container-id', 'quote-apply-widget-container');
        script.async = true;

        script.onload = () => {
          setWidgetLoaded(true);
          setWidgetError(false);
          scriptLoadedRef.current = true;
        };

        script.onerror = () => {
          console.error('Failed to load Quote & Apply widget script');
          setWidgetError(true);
          setWidgetLoaded(false);
        };

        document.body.appendChild(script);
      } catch (error) {
        console.error('Error loading Quote & Apply widget:', error);
        setWidgetError(true);
      }
    };

    loadWidget();

    // Cleanup function - do not remove script to avoid reloading on navigation
    return () => {
      // Keep script in DOM for navigation persistence
    };
  }, []);

  // Reinitialize container if navigating back
  useEffect(() => {
    if (containerRef.current && widgetLoaded && scriptLoadedRef.current) {
      // Widget should reinitialize automatically when container is available
    }
  }, [widgetLoaded]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-pink-700 to-pink-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <Heart className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-pink-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-pink-50">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* Educational Section - Hero */}
      <section className="py-16 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {content.educationalSection.title}
            </h2>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              {content.educationalSection.description}
            </p>
          </div>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            {content.educationalSection.whyImportant.title}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {content.educationalSection.whyImportant.cards.map((card, index) => {
              const Icon = iconMap[card.icon] || Heart;
              return (
                <Card key={index} className="text-center border-none shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-pink-600 to-pink-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {card.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Types of Insurance */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {content.educationalSection.types.title}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="border-l-4 border-pink-600">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {content.educationalSection.types.term.name}
                  </h3>
                  <p className="text-gray-700">
                    {content.educationalSection.types.term.description}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-purple-600">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {content.educationalSection.types.permanent.name}
                  </h3>
                  <p className="text-gray-700">
                    {content.educationalSection.types.permanent.description}
                  </p>
                </CardContent>
              </Card>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
              <p className="text-gray-700">
                <strong className="text-blue-900">Note:</strong> {content.educationalSection.types.note}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Needs Assessment */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-6 text-gray-900">
              {content.educationalSection.needsAssessment.title}
            </h2>
            <p className="text-center text-gray-600 mb-8">
              {content.educationalSection.needsAssessment.description}
            </p>
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 p-6 rounded-lg">
              <ul className="space-y-3">
                {content.educationalSection.needsAssessment.factors.map((factor, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{factor}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Frequently Asked Questions' : 'Preguntas Frecuentes'}
            </h2>
            <div className="space-y-6">
              {content.educationalSection.faq.map((item, index) => (
                <Card key={index} className="shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-6 h-6 text-pink-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {item.question}
                        </h3>
                        <p className="text-gray-700">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Examples */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Coverage Examples' : 'Ejemplos de Cobertura'}
            </h2>
            <div className="grid gap-4">
              {content.coverageExamples.map((example, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-pink-600 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote & Apply Widget Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
              {language === 'en' ? 'Get Your Life Insurance Quote' : 'Obtenga Su Cotización de Seguro de Vida'}
            </h2>
            <p className="text-center text-gray-600 mb-8">
              {language === 'en' 
                ? 'Complete the form below to get started' 
                : 'Complete el formulario a continuación para comenzar'}
            </p>

            {/* Disclaimer */}
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  <strong>{language === 'en' ? 'Important:' : 'Importante:'}</strong> {content.educationalSection.disclaimer}
                </p>
              </div>
            </div>

            <Card className="shadow-2xl border-none">
              <CardContent className="p-8 md:p-12">

                {/* Quote & Apply Widget Container */}
                {!widgetError ? (
                  <div className="quote-apply-widget-wrapper">
                    {!widgetLoaded && (
                      <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-pink-700"></div>
                        <p className="mt-4 text-gray-600">
                          {language === 'en' ? 'Loading quote form...' : 'Cargando formulario de cotización...'}
                        </p>
                      </div>
                    )}
                    {/* Container ID must match data-strife-container-id attribute */}
                    <div id="quote-apply-widget-container" ref={containerRef} className="w-full"></div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-amber-900 font-semibold mb-3">
                          {language === 'en' 
                            ? 'Quote form temporarily unavailable.' 
                            : 'Formulario de cotización temporalmente no disponible.'}
                        </p>
                        <p className="text-sm text-amber-800 mb-4">
                          {language === 'en'
                            ? 'Please use our direct application link below, or contact us for assistance.'
                            : 'Por favor use nuestro enlace de solicitud directo a continuación, o contáctenos para ayuda.'}
                        </p>
                        <a 
                          href="https://app.back9ins.com/apply/palerminsurance"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-pink-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-pink-800 transition-colors shadow-md"
                        >
                          {language === 'en' ? 'Open Direct Application' : 'Abrir Solicitud Directa'}
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alternative Link */}
                <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    {language === 'en' ? 'Or use our alternative application portals:' : 'O use nuestros portales alternativos de solicitud:'}
                  </p>
                  
                  {/* LifeLink - Simplicity Group Option */}
                  <div className="mb-4">
                    <a 
                      href={content.lifeLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-600 to-pink-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-pink-700 hover:to-pink-800 transition-all shadow-md hover:shadow-lg"
                    >
                      <ExternalLink className="w-5 h-5" />
                      {language === 'en' ? 'Explore life insurance - LifeLink' : 'Ver opciones de vida - LifeLink'}
                    </a>
                    <p className="text-xs text-gray-500 mt-2">LifeLink – Simplicity Group</p>
                  </div>

                  {/* Back9 Option */}
                  <div>
                    <a 
                      href={content.back9Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-pink-700 font-semibold hover:text-pink-800 transition-colors text-sm underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {language === 'en' ? 'Open Back9 Application' : 'Abrir Solicitud Back9'}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What to Prepare */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {content.whatToPrepare.title}
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
              <ul className="space-y-3">
                {content.whatToPrepare.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-pink-700 to-pink-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {language === 'en' ? 'Need Help or Have Questions?' : '¿Necesita Ayuda o Tiene Preguntas?'}
            </h2>
            <p className="text-xl text-pink-100 mb-8">
              {language === 'en' 
                ? 'Our team is here to assist you with your life insurance needs' 
                : 'Nuestro equipo está aquí para ayudarle con sus necesidades de seguro de vida'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-pink-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-pink-50 transition-all shadow-lg"
              >
                <MessageCircle className="w-6 h-6" />
                {language === 'en' ? 'Chat via WhatsApp' : 'Chatear por WhatsApp'}
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-pink-800 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-pink-900 transition-all shadow-lg"
              >
                <Phone className="w-6 h-6" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LifeInsurance;